/* -------------------------------------------------------------------------------------------------
Restricted - Copyright (C) Siemens Healthcare GmbH/Siemens Medical Solutions USA, Inc., 2021. All rights reserved
------------------------------------------------------------------------------------------------- */

import { css, html, LitElement } from 'lit-element';
import { sharedStyles } from '../../styles';
import { dispatchAttributeChangedEvent } from '../../utils/attribute-changed-event-dispatcher.js';
import { ControlStateMixin } from '../../utils/control-state-mixin.js';
import { dispatchCustomEvent } from '../../utils/custom-event-dispatcher.js';
import { deviceIdentifier } from '../../utils/device-identifier.js';
import { FocusBlurMixin } from '../../utils/focus-blur-mixin.js';
import { KEYCODE } from '../../utils/keycode.js';

/**
 * @prop {String} label - Defines the label shown at the Tab Item.
 * @prop {String} name - Will be displayed as a label if the tab is collapsed and the label property is undefined.
 * @prop {String} icon - Defines an icon to be shown inside the Tab Item.
 * @prop {String} size - Default s. Defines the size of an active tab. Supports two values l (23.5rem) and s (17.5rem).
 * @prop {Boolean} sticky - Default false. If set to `true` the tab will not have the closing functionality (close icon will be hidden).
 * @prop {String} info - Shows an info text below the label. Only applicable if the patient property is true.
 * @prop {Boolean} patient - Default false. Defines whether the Tab Item is of a patient type or not. If set to `true`, the icon property will overwrite the default icon, and the info property will be available.
 * @prop {Boolean} active - Default false. Defines whether a Tab Item is active or not. The style will change to represent the active state.
 * @prop {Boolean} disabled - Default false. If set to `true`, the Tab Item will be disabled and thus not allow clicks. The style will change to represent the disabled state.
 * @prop {String} href - Defines a path to which navigation will occur on click.
 * @prop {String} badge - If set, will be shown as badge for tab item.
 * @prop {String} closable - If set, close icon will be shown for tab item.
 * @prop {Boolean} collapsed - *hide
 * @prop {String} role - *hide
 * @slot functions - For patient tabs, this slot holds function icons such as report and data protection. Is only visible on active tabs.
 * @fires clicked - Dispatched when clicked on the tab item.
 * @fires closed - Dispatched when a patient tab's 'close' item is clicked.
 */

export class SHTabItem extends FocusBlurMixin(ControlStateMixin(LitElement)) {
  static get properties() {
    return {
      label: { type: String, reflect: true },
      name: { type: String, reflect: true },
      info: { type: String, reflect: true },
      href: { type: String, reflect: true },
      patient: { type: Boolean, reflect: true },
      active: { type: Boolean, reflect: true },
      disabled: { type: Boolean, reflect: true },
      role: { type: String, reflect: true },
      icon: { type: String, reflect: true },
      size: { type: String, reflect: true },
      collapsed: { type: Boolean, reflect: true },
      /** @ignore */
      condensed: { type: Boolean, reflect: true },
      sticky: { type: Boolean, reflect: true },
      /** @ignore */
      _accessContext: {
        type: Boolean,
        reflect: true,
        attribute: 'access-context',
      },
      /** @ignore */
      _footerContext: {
        type: Boolean,
        reflect: true,
        attribute: 'footer-context',
      },
      badge: { type: String, reflect: true },
      closable: { type: Boolean, reflect: true },
    };
  }

  static get styles() {
    return [
      sharedStyles,
      css`
        :host {
          font: var(--title-1);
          color: var(--text-secondary);
          position: relative;
          overflow: hidden;
          outline: 0;
          max-width: 11.5rem;
          min-width: 3.5rem;
          height: 3.5rem;
          box-sizing: border-box;
          padding: 1rem 1rem 0.875rem 1rem;
          line-height: 1.5rem;
          border-bottom: 0.125rem solid transparent;
          transition: var(--transition-time) border ease-in-out;
          text-align: center;
          cursor: pointer;
          -webkit-font-smoothing: antialiased;
          text-rendering: optimizeLegibility;
          -moz-osx-font-smoothing: grayscale;
          user-select: none;
          -moz-user-select: none;
          -webkit-user-select: none;
          display: flex;
        }

        :host(:not([label])) {
          min-width: 4rem;
        }
        :host([disabled]) {
          background: transparent;
          color: var(--text-disabled);
          pointer-events: none;
        }
        .text {
          overflow: hidden;
          font-size: 0.875rem;
        }
        :host([href]) .href-wrapper {
          margin: 1rem;
        }
        :host([patient][href]) .href-wrapper,
        :host([href][badge]:not([icon])) .href-wrapper {
          display: flex;
        }
        :host([icon][href][access-context]:not([label]):not([condensed])) .href-wrapper {
          margin: 1.25rem 1rem;
        }
        :host([access-context][patient][href]) .href-wrapper {
          margin: 1.25rem 0.5rem;
        }
        :host([access-context][href]) .href-wrapper {
          margin: 1.5rem 1rem;
        }
        :host([condensed][href]) .href-wrapper {
          margin: 0.5rem 1rem;
        }
        :host([condensed][href][patient]) .href-wrapper {
          margin: 0.25rem 0.5rem;
        }
        :host([icon][access-context][href]) .href-wrapper,
        :host([icon][href]:not([label]):not([condensed])) .href-wrapper {
          margin: 0.75rem 1rem;
        }
        :host([icon][href]) .href-wrapper,
        :host([icon][access-context][href][condensed]) .href-wrapper {
          margin: 0.25rem 1rem 0.125rem;
        }
        :host([icon][access-context][href][condensed]),
        :host([icon][href][label]),
        :host([icon][href][access-context]),
        :host([icon]:not([label])[href]) {
          padding: 0rem;
        }
        :host a {
          color: currentColor;
          text-decoration: none;
          height: 3.5rem;
          margin: -1rem;
          width: calc(100% + 2rem);
        }
        :host([access-context][href]) a {
          height: 4.5rem;
          margin: -1.5rem -1rem;
        }
        :host([access-context][patient][href]) a {
          margin: -1.5rem -0.5rem;
          width: calc(100% + 1rem);
        }
        :host([condensed][href]) a {
          height: 2.5rem;
          margin: -0.5rem -1rem;
        }
        :host([condensed][patient]) a {
          margin: -0.25rem -0.5rem;
        }
        :host([icon][href]) a {
          margin: 0rem;
        }

        .label,
        .info,
        .name {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          transition: var(--transition-time) all ease-in-out;
        }

        :host([active]) #tab-item-icon {
          color: var(--text-primary);
        }
        :host([disabled]) #tab-item-icon {
          color: var(--text-disabled);
        }
        :host([patient][disabled]) #close {
          display: none;
        }

        :host([icon]) #tab-item:not([collapsed]) {
          padding: 0.25rem 1rem 0.125rem 1rem;
          line-height: 1rem;
          font-size: 0.75rem;
        }
        :host([icon]:not([label])) {
          padding: 0.75rem 1rem 0.625rem 1rem;
        }
        :host([active]) {
          color: var(--text-primary);
          border-bottom: 0.125rem solid rgba(var(--ui-2), var(--opacity-1));
        }
        #tab-item-icon {
          color: var(--text-secondary);
        }
        :host([patient]) #tab-item-icon {
          margin-right: 0.5rem;
          margin-left: 0rem;
        }

        /* functions slot */
        :host([href]:not([active])) slot[name='functions']::slotted(*) {
          display: none;
        }
        :host([href][patient]) slot[name='functions']::slotted(*),
        :host([patient]) > ::slotted(*[slot='functions']) {
          width: 2rem;
          margin-right: 1.5rem;
          margin-left: -0.5rem;
        }
        :host(:not([active])) > ::slotted(*[slot='functions']) {
          width: 0rem;
          display: none;
          min-width: unset;
          margin: 0rem;
          opacity: 0;
        }

        /* patient tab */
        sh-icon#close {
          opacity: 0;
          position: absolute;
          top: 0rem;
          right: 0.5rem;
        }
        :host([patient]) .text {
          margin-right: 1.5rem;
        }
        sh-icon[icon='patient'] {
          margin-right: 0.5rem;
        }
        :host([patient]) sh-icon[icon='patient'] {
          display: flex;
          color: var(--text-secondary);
        }
        :host([patient][active]) sh-icon[icon='patient'] {
          color: var(--text-primary);
        }
        :host([patient]) {
          display: flex;
          padding: 1.25rem 0.5rem 1.125rem 0.5rem !important;
          line-height: 1rem;
          text-align: left;
          align-items: center;
          width: fit-content;
          min-width: 11.5rem;
          max-width: 11.5rem;
        }
        :host([patient][active]) {
          max-width: 17.5rem;
        }
        :host([patient]) .label {
          font: var(--title-1);
        }
        :host([patient]) .info {
          display: block;
          font: var(--body-2);
          line-height: 1rem;
        }

        :host([disabled]) .info {
          color: var(--text-disabled);
        }
        :host([disabled]) sh-icon[icon='patient'] {
          color: var(--text-disabled);
        }
        :host([patient][condensed]) {
          padding: 0.25rem 0.5rem 0.125rem 0.5rem !important;
        }
        :host([icon][label]) {
          padding: 0.25rem 1rem 0.125rem 1rem;
          line-height: 1rem;
        }
        :host([icon][label]) #tab-item-icon {
          display: block;
        }
        :host([patient][active][size='l']) {
          max-width: 23.5rem;
        }

        /* access bar */
        :host([icon][label][access-context]) #tab-item-icon,
        :host([icon][label][footer-context]) #tab-item-icon {
          display: block;
        }
        :host([access-context]) {
          padding: 1.5rem 1rem 1.375rem;
          height: 4.5rem;
        }
        :host([icon][label][access-context]:not([href])) {
          padding: 0.75rem 1rem 0.625rem;
        }
        :host([icon][access-context]:not([label]):not([href])) {
          padding: 1.25rem 1rem 1.125rem;
        }
        :host([condensed]) {
          padding: 0.5rem 1rem 0.375rem 1rem;
          height: 2.5rem;
          display: flex;
        }
        :host([icon][label][condensed]) {
          line-height: 1.5rem;
        }
        :host([icon][condensed]),
        :host([icon][condensed]:not([label]):not([href])),
        :host([icon][label][condensed][access-context]:not([href])) {
          padding: 0.25rem 1rem 0.125rem;
        }

        /* collapsed */
        :host([collapsed]) {
          height: 2.5rem;
          padding: 0.5rem;
          width: 100%;
          max-width: none;
          text-align: left;
          font-weight: 500;
          border: 0rem transparent;
          display: flex;
        }
        :host([patient][collapsed]) {
          height: 3.5rem;
        }
        :host([icon][collapsed]),
        :host([patient][collapsed]:not([info])) {
          height: 3rem;
        }
        :host([patient][collapsed]:hover:not(.touch-device)) #close {
          display: block;
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          right: 0.5rem;
          background: transparent;
        }
        :host([active][collapsed]) {
          background: rgba(var(--ui-1), var(--opacity-6));
        }
        :host([collapsed]:not([label])) .name {
          display: flex;
          font: var(--body-1);
        }
        :host([collapsed]:not([label])) {
          padding: 0.5rem !important;
        }
        :host([collapsed]:not([patient])) .name,
        :host([collapsed]:not([patient])) .label {
          color: var(--text-primary);
          line-height: 1.5rem;
        }
        :host([icon][collapsed]) .name,
        :host([icon][collapsed]) .label {
          line-height: 2rem;
        }
        :host([collapsed]) #tab-item-icon {
          margin: 0 0.5rem 0 0;
        }

        /* hover */
        :host(:hover:not(.touch-device)) #tab-item-icon,
        :host(.touch-device):hover #tab-item-icon {
          color: var(--text-primary);
        }
        :host(:hover:not(.touch-device)) .label,
        :host(.touch-device):hover .label {
          color: var(--text-primary);
        }
        :host([patient]:hover:not(.touch-device)) #close,
        :host([patient][active].touch-device) #close,
        :host([patient].touch-device) #close,
        #close:focus {
          opacity: 1;
          top: 0.5rem;
        }
        #close.item-focused {
          outline: var(--focus-outline);
        }
        :host([patient]:hover:not(.touch-device)) sh-icon[icon='patient'],
        :host([patient].touch-device):hover sh-icon[icon='patient'] {
          color: var(--text-primary);
        }
        :host([icon]:not([patient])) {
          display: block;
        }
        :host([icon][label]:not([patient])) #tab-item-icon {
          margin: auto;
        }
        :host([icon][access-context]) sh-badge {
          position: absolute;
          right: 0.375rem;
          top: 0.375rem;
          margin: 0rem;
        }
        :host([icon][access-context][badge='']) sh-badge {
          position: absolute;
          right: 0.5rem;
          top: 0.5rem;
          margin: 0rem;
        }
        :host([icon][badge='']) sh-badge,
        :host([icon]) sh-badge {
          position: absolute;
          right: 0.25rem;
          top: 0.25rem;
          margin: 0rem;
        }
        sh-icon:not(#close) {
          transition: 0s color;
        }
        :host([access-context]) sh-badge {
          margin-top: -1.125rem;
        }
        :host([access-context][badge='']) sh-badge {
          margin-top: -1rem;
        }
        :host(:not([patient]):not([icon])) {
          overflow: unset;
        }
        sh-badge {
          margin: -0.625rem -0.625rem 0rem 0.375rem;
          transition: var(--transition-time) all ease-in-out;
        }
        :host([badge='']) sh-badge {
          margin: -0.5rem -0.5rem 0rem 0.5rem;
        }
        :host([condensed]) sh-badge {
          margin-top: -0.125rem;
        }
        :host([condensed][badge='']) sh-badge {
          margin-top: 0rem;
        }
        :host([active].item-focused) {
          box-shadow: var(--focus-color) 0px 0.125rem 0px 0px inset,
            var(--focus-color) 0.125rem 0px 0px 0px inset,
            var(--focus-color) -0.125rem 0px 0px 0px inset;
          outline: none;
        }
        :host(.item-focused) {
          box-shadow: none;
          outline: var(--focus-outline);
          outline-offset: -0.125rem;
        }
        .tab-close,
        :host([access-context][closable][disabled][condensed]) sh-badge {
          margin-left: 0.5rem;
        }
        :host([closable]:not([disabled])) {
          padding-right: 0.5rem;
        }
        :host([access-context][closable]:not([disabled])) {
          padding-right: 2rem;
        }
        :host([access-context][closable]:not([disabled])) .tab-close {
          margin: 0rem;
          position: absolute;
          top: 0.5rem;
          right: 0.5rem;
        }
        :host([access-context][closable]:not([disabled])) .label {
          margin-right: 0.5rem;
        }

        :host([access-context][closable][condensed]) sh-badge,
        :host([access-context][closable][condensed]) sh-badge {
          margin: -0.125rem 0 0 0;
        }
        :host([access-context][closable]) sh-badge {
          margin: -1.125rem 0 0 0;
        }
      `,
    ];
  }

  constructor() {
    super();
    this.size = 'm';
  }

  render() {
    return html`
      ${this.href
        ? html`<a href="${this.href}">
            <div class="href-wrapper">${this._getTabItemTemplate()}</div>
          </a>`
        : this._getTabItemTemplate()}
      ${!this.sticky && this.patient
        ? html` <sh-icon
            icon="cancel"
            button
            size="s"
            @click="${(e) => this._removeTab(e)}"
            id="close"
            tabindex="0"
          ></sh-icon>`
        : ''}
    `;
  }

  _getTabItemTemplate() {
    return html`${this.patient && !this.icon
      ? html` <sh-icon icon="patient"></sh-icon>`
      : this.icon
      ? html` <sh-icon icon="${this.icon}" id="tab-item-icon"></sh-icon>`
      : ''}
    ${this.label || this.name || this.info
      ? html` <div class="text">
          ${this.label
            ? html` <div class="label" @mouseenter="${(e) => this._showTooltip(e, this.label)}">
                ${this.label}
              </div>`
            : ''}
          ${this.collapsed && !this.label && this.name
            ? html` <div class="name">${this.name}</div>`
            : ''}
          ${this.patient && this.info
            ? html` <div class="info" @mouseenter="${(e) => this._showTooltip(e, this.info)}">
                ${this.info}
              </div>`
            : ''}
          <sh-tooltip placement="bottom-left"></sh-tooltip>
        </div>`
      : ''}
    ${this.patient ? html` <slot name="functions"></slot>` : ''}
    ${!this.patient &&
    this.badge !== null &&
    (this.badge === 'error' || this.badge === 'success' || this.badge === 'warning')
      ? html` <sh-badge type="${this.badge}" ?disabled="${this.disabled}"></sh-badge>`
      : ''}
    ${!this.patient && this.badge !== null && !isNaN(this.badge) && this.badge !== ''
      ? html` <sh-badge label="${this.badge}" ?disabled="${this.disabled}"></sh-badge>`
      : ''}
    ${!this.patient && this.badge !== null && this.badge === '' && this.closable
      ? html` <sh-badge ?disabled="${this.disabled}"></sh-badge>`
      : ''}
    ${!this.patient && this.closable && !this.disabled
      ? html` <sh-icon
          icon="cancel"
          size="s"
          button
          ?disabled="${this.disabled}"
          class="tab-close"
          @click="${(e) => this._removeTab(e)}"
        ></sh-icon>`
      : ''}
    ${!this.patient && this.badge !== null && this.badge === '' && !this.closable
      ? html` <sh-badge ?disabled="${this.disabled}"></sh-badge>`
      : ''} `;
  }

  connectedCallback() {
    super.connectedCallback();
    if (!this.hasAttribute('role')) {
      this.role = 'tab';
    }
    if (deviceIdentifier.isTouchDevice()) {
      this.classList.add('touch-device');
    }
  }

  firstUpdated() {
    super.firstUpdated();
    this._setContext();
    this.addEventListener('click', this._clickEventListener);
    //only active tab is focusable
    if (!this.active) {
      this.setAttribute('tabindex', '-1');
    } else {
      this.setAttribute('tabindex', '0');
    }
  }

  updated(changedProperties) {
    super.updated(changedProperties);
    //if active changes adjust tabindex
    if (changedProperties.has('active')) {
      if (!this.active) {
        this.setAttribute('tabindex', '-1');
      } else {
        this.setAttribute('tabindex', '0');
      }
    }
    const listOfProperties = [
      'label',
      'name',
      'info',
      'href',
      'patient',
      'active',
      'disabled',
      'role',
      'icon',
      'size',
      'collapsed',
      'condensed',
      'sticky',
      'badge',
    ];
    dispatchAttributeChangedEvent(this, changedProperties, listOfProperties);
  }
  _showTooltip(e, content) {
    const tooltip = this.shadowRoot.querySelector('sh-tooltip');
    if (
      e.target.offsetWidth < e.target.scrollWidth &&
      !tooltip.classList.contains('touch-device')
    ) {
      tooltip.target = e.target;
      tooltip.label = content;
      tooltip.visible = true;
    }
  }
  _setContext() {
    const tabs = this.closest('sh-tabs');
    if (tabs) {
      this._accessContext = tabs._accessContext;
      this._footerContext = tabs._footerContext;
    }
  }

  _clickEventListener() {
    this._dispatchClickedEvent();
    if (this.href === undefined || this.href === null) return;
    window.location.href = this.href;
  }

  _removeTab(e) {
    e.stopPropagation();
    dispatchCustomEvent(this, 'closed');
  }

  _dispatchClickedEvent() {
    dispatchCustomEvent(this, 'clicked');
    this.active = true;
  }

  /**
   *
   * Overriding handleKeyup function of
   * FocusBlurMixin to satisfy requirements
   * of tab component.
   *
   */
  handleKeyup(e) {
    let siblings = this.parentElement.querySelectorAll('SH-TAB-ITEM:not([disabled])');
    let target;
    if (e.keyCode === KEYCODE.TAB && !this.disabled) {
      if (document.activeElement === this && !this.shadowRoot.activeElement) {
        this.classList.add('item-focused');
      } else {
        this.classList.remove('item-focused');
      }
    }
    if (
      (e.keyCode === KEYCODE.SPACE || e.keyCode === KEYCODE.ENTER) &&
      document.activeElement === this &&
      !this.shadowRoot.activeElement
    ) {
      this._dispatchClickedEvent();
    } else if (e.keyCode === KEYCODE.ARROW_RIGHT || e.keyCode === KEYCODE.ARROW_DOWN) {
      //next not disabled sibling
      let posThis = Array.from(siblings).indexOf(this);
      if (posThis === siblings.length - 1) {
        target = siblings[0];
      } else {
        target = siblings[posThis + 1];
      }
      this.handleArrowInput(e, target);
    } else if (e.keyCode === KEYCODE.ARROW_LEFT || e.keyCode === KEYCODE.ARROW_UP) {
      //previous not disabled sibling
      let posThis = Array.from(siblings).indexOf(this);
      if (posThis === 0) {
        target = siblings[siblings.length - 1];
      } else {
        target = siblings[posThis - 1];
      }
      this.handleArrowInput(e, target);
    }
  }
  handleArrowInput(e, target) {
    if (target) {
      e.preventDefault();
      target.focus();
      target.classList.add('item-focused');
    }
  }
}

customElements.define('sh-tab-item', SHTabItem);
